import {
  buildFilters,
  fixLogoAltText,
  fixSliderForFewSlides,
  hideOnEmpty,
  installLogoOrganizationInterceptor,
  stripCountryInLocationStrings,
} from '../../../javascripts/utils/jobiqo';

// Install the fetch interceptor at module load time — before jobiqoJobWidget.init()
// is ever called — so the logo→company map is ready when the widget renders cards.
const logoOrganizationMap = installLogoOrganizationInterceptor();

const configEl = document.querySelector<HTMLScriptElement>('[data-jobiqo-cards-config]');

if (configEl) {
  const { widgetId, tabs: tabConfigs, backendDomain, baseUrl } = JSON.parse(configEl.textContent ?? '{}');

  const loadTab = (tabIndex: number): void => {
    // eslint-disable-next-line security/detect-object-injection -- Safe: tabIndex is a controlled number
    const config = tabConfigs[tabIndex];

    const section = document.querySelector<HTMLElement>(`[data-jobico-widget="${widgetId}"]`);
    if (section && config.color) {
      section.style.setProperty('--jobico-card-bg-color', config.color);
    }

    if (typeof jobiqoJobWidget === 'undefined') {
      return;
    }

    const filters: Record<string, unknown> = {};

    if (config.filter.searchTerm) {
      // eslint-disable-next-line security/detect-object-injection -- Safe: data from trusted CMS config
      filters.what = config.filter.searchTerm;
    }

    const { taxonomy, extraConditions } = buildFilters(config.filter);
    // eslint-disable-next-line security/detect-object-injection -- Safe: data from trusted CMS config
    if (taxonomy) filters.taxonomy = taxonomy;
    // eslint-disable-next-line security/detect-object-injection -- Safe: data from trusted CMS config
    if (extraConditions) filters.extraConditions = extraConditions;

    const jobiqoWidgetConfig = {
      // Falls back to location.origin when env vars are empty (reverse-proxy mode)
      backendDomain: backendDomain ?? window.location.origin,
      baseUrl: baseUrl ?? window.location.origin,
      showSearchBar: false,
      showSearchResult: true,
      utmParameters: 'false', // disable default utm params
      slider: config.layout === 'slider',
      jobListColumns:
        config.layout === 'grid' ? { large: 2, medium: 2 } : undefined,
      slidesResponsive:
        config.layout === 'slider'
          ? [
              { breakpoint: 1023, settings: { slidesToShow: 3 } },
              { breakpoint: 767, settings: { slidesToShow: 1 } },
            ]
          : undefined,
      jobAmount: config.limit,
      filters,
      styles: {
        fontFamily: '"Work Sans", Helvetica, Arial, sans-serif',
        order: ['title', 'location'],
      },
    };
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console -- Dev-only debug logging
      console.log('jobiqoWidgetConfig', jobiqoWidgetConfig);
    }
    jobiqoJobWidget.init(jobiqoWidgetConfig);

    if (section) {
      // Show/hide per-tab buttons
      const allTabButtons = section.querySelectorAll<HTMLElement>('[data-tab-button]');
      allTabButtons.forEach((btn) => {
        // eslint-disable-next-line no-param-reassign -- Direct DOM property update required
        btn.hidden = true;
      });
      const activeTabButton = section.querySelector<HTMLElement>(
        `[data-tab-button="${tabIndex}"]`,
      );
      if (activeTabButton) {
        activeTabButton.hidden = false;
      }
    }
  };

  const initWidget = (): void => {
    loadTab(0);

    const widgetContainer = document.querySelector<HTMLElement>(`[data-jobico-widget="${widgetId}"]`)!;

    const widgetEl = widgetContainer.querySelector<HTMLElement>('#jobiqo_job_widget');
    if (widgetEl) {
      hideOnEmpty(widgetEl, widgetContainer);
      stripCountryInLocationStrings(widgetEl);
      fixLogoAltText(widgetEl, logoOrganizationMap);
      if (tabConfigs[0]?.layout === 'slider') {
        fixSliderForFewSlides(widgetEl);
      }
    }

    const tabLinks = widgetContainer.querySelectorAll<HTMLAnchorElement>('.tabs__tab-link');
    const tabItems = widgetContainer.querySelectorAll<HTMLElement>('.tabs__tab');

    tabLinks.forEach((link, index) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();

        tabItems.forEach((item) => item.classList.remove('tabs__tab--active'));
        // eslint-disable-next-line security/detect-object-injection -- Safe: index is a controlled number from forEach
        tabItems[index].classList.add('tabs__tab--active');

        tabLinks.forEach((lnk) => lnk.removeAttribute('aria-selected'));
        link.setAttribute('aria-selected', 'true');

        loadTab(index);
      });
    });
  };

  if (typeof jobiqoJobWidget !== 'undefined') {
    initWidget();
  } else {
    document.addEventListener('jobiqoWidgetLoaded', initWidget);
  }
}
